import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./FichaProductor.scss";
import Badge from "../../../components/common/Badge/Badge";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../../components/common/Table/Table";
import DetalleLayout from "../../../Layouts/DetalleLayout/DetalleLayout";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { mapList } from "../../../utils/utils";

export default function FichaProductor({ user }) {
    const { id } = useParams();
    const [productor, setProductor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [cumplimientoLegal, setCumplimientoLegal] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const { selectedZone } = useZone();

    const fetchProductor = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/Productores/GetProductorById/${id}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentos = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/ProductoresDocumento/GetDocumentosFromProductor/${id}`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchCumplimientoLegal = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.post(
                `/api/CumplimientoLegal/GetCumplimientoByProductor/${id}?CodZona=${selectedZone.value}&CodZonaLegal=1`
            );
            console.log(`Response from fetchGestor:`, response.data);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            const response = await api.get(
                `/api/Paises/GetPaisesByZona/${selectedZone.value}`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            const response = await api.get(
                `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
            );
            return response.data;
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                const productor = await fetchProductor(id);

                if (isMounted) {
                    setProductor(productor);
                }

                const results = await Promise.allSettled([
                    fetchDocumentos(id),
                    fetchCumplimientoLegal(id),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                ]);

                const [
                    documentosResult,
                    cumplimientoLegalResult,
                    paisesResult,
                    comunidadesResult,
                    provinciasResult,
                ] = results;

                if (isMounted) {
                    if (documentosResult.status === "fulfilled") {
                        setDocumentos(documentosResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar los documentos:",
                            documentosResult.reason
                        );
                    }

                    if (cumplimientoLegalResult.status === "fulfilled") {
                        setCumplimientoLegal(cumplimientoLegalResult.value);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            cumplimientoLegalResult.reason
                        );
                    }

                    if (paisesResult.status === "fulfilled") {
                        setPaises(
                            mapList(
                                paisesResult.value,
                                "pais",
                                "codPais",
                                "codPais"
                            )
                        );
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            paisesResult.reason
                        );
                    }

                    if (comunidadesResult.status === "fulfilled") {
                        setComunidades([
                            ...mapList(
                                comunidadesResult.value,
                                "comunidad",
                                "codComunidad",
                                "codComunidad",
                                "codPais"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            comunidadesResult.reason
                        );
                    }

                    if (provinciasResult.status === "fulfilled") {
                        setProvincias([
                            ...mapList(
                                provinciasResult.value,
                                "provincia",
                                "codProvincia",
                                "codProvincia",
                                "codComunidad"
                            ),
                        ]);
                    } else {
                        console.warn(
                            "No se pudieron cargar las eléctricas:",
                            provinciasResult.reason
                        );
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos principales. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en datos críticos de loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [id, selectedZone]);

    const getEstadoBadge = (estado) => {
        const estadoConfig = {
            VALIDADO: { type: "success", label: "Validado" },
            PENDIENTE: { type: "info", label: "Pendiente" },
            INCIDENCIA: { type: "warning", label: "Incidencia" },
            VENCIDO: { type: "error", label: "Vencido" },
            ACTIVA: { type: "success", label: "Activa" },
        };
        return estadoConfig[estado] || estadoConfig["PENDIENTE"];
    };

    const tabsGeneral = [
        { id: "informacion", label: "Información General" },
        { id: "localizacion", label: "Localización" },
    ];

    const tabsDatos = [
        { id: "documentacion", label: "Documentación" },
        { id: "cumplimiento", label: "Cumplimiento legal" },
    ];

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Nombre:</label>
                <span>{productor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>CIF:</label>
                <span>{productor.cif}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Contacto:</label>
                <span>{productor.contacto}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Teléfono:</label>
                <span>{productor.telefono}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Email:</label>
                <span>{productor.eMail}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Nombre representante:</label>
                <span>{productor.nombreRepresentante}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Apellidos representante:</label>
                <span>{productor.apellidosRepresentante}</span>
            </div>
        </div>
    );

    const renderLocalizacion = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>Dirección:</label>
                <span>{productor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Localidad:</label>
                <span>{productor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Provincia:</label>
                <span>{productor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>Comunidad:</label>
                <span>{productor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>País:</label>
                <span>{productor.pais}</span>
            </div>
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            case "informacion":
                return renderInformacionGeneral();
            case "localizacion":
                return renderLocalizacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "documentacion":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: "Tipo de documento",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: "Comunidad",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "provincia",
                                    label: "Provincia",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: "Año",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "nombreArchivo",
                                    label: "Documento",
                                    visible: true,
                                    sortable: true,
                                    render: (row) => (
                                        <a href="#" className="documento-link">
                                            {row.nombreArchivo}
                                        </a>
                                    ),
                                },
                                {
                                    key: "codEstado",
                                    label: "Validación",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage="No hay documentos disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Eliminar"]}
                            elementName="Documento"
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "info", label: "Pendiente de validar" },
                                {
                                    type: "warning",
                                    label: "Validación con incidencias",
                                },
                                { type: "success", label: "Validado" },
                            ]}
                            formElements={[
                                {
                                    elements: [
                                        {
                                            name: "tipoDocumento",
                                            type: "radio",
                                            label: "Alta pequeño productor",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "tipoDocumento",
                                            type: "radio",
                                            label: "Declaración anual",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "codPais",
                                            type: "select",
                                            label: "País",
                                            placeholder: "Seleccione el país",
                                            options: paises,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "codComunidad",
                                            type: "select",
                                            label: "Comunidad",
                                            placeholder:
                                                "Seleccione la comunidad",
                                            options: comunidades,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement: "codPais",
                                        },
                                        {
                                            name: "codProvincia",
                                            type: "select",
                                            label: "Provincia",
                                            placeholder:
                                                "Seleccione la provincia",
                                            options: provincias,
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            associatedFormElement:
                                                "codComunidad",
                                        },
                                        {
                                            name: "codZona",
                                            type: "number",
                                            label: "Zona",
                                            visible: false,
                                            defaultValue: "1",
                                        },
                                        {
                                            name: "fichero",
                                            type: "document",
                                            label: "Documento",
                                            placeholder: "Subir documento",
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "observaciones",
                                            type: "textarea",
                                            label: "Observaciones",
                                            placeholder:
                                                "Ingrese observaciones adicionales",
                                            required: false,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            rows: 3,
                                        },
                                    ],
                                },
                            ]}
                            addApiUrl="/api/Productores/SetProductor"
                            deleteApiUrl="/api/Productores/DeleteProductor"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                        />
                    </div>
                );
            case "cumplimiento":
                return (
                    <div className="ficha-gestor__tarifas">
                        <Table
                            columns={[
                                {
                                    key: "nombre",
                                    label: "Descripción",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "comunidad",
                                    label: "Comunidad",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "año",
                                    label: "Año",
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: "Estado",
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={cumplimientoLegal}
                            noResultsMessage="No hay cumplimientos legales disponibles"
                            rowsPerPage={10}
                            showCheckboxes={false}
                            elementName="Cumplimiento legal"
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "" },
                                { type: "danger", label: "No presentado" },
                                {
                                    type: "warning",
                                    label: "Validado con incidencias",
                                },
                                { type: "success", label: "Validado" },
                            ]}
                            loading={loading}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={productor ? `Productor ${productor.nombre}` : "Cargando..."}
            backLink="/administracion/productores"
            backText="Volver a Productores"
            loading={loading}
            error={error}
            notFound={!productor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && productor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Información General</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>Datos del productor</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}
        </DetalleLayout>
    );
}
